import { useQuery } from "@apollo/client/react";
import { useMemo, useRef } from "react";

import { getFiltersPredicationFromURI } from "components/Filters/helpers";
import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import { getSearchQuery } from "components/SearchInput/helpers";
import useURLParams from "hooks/useURLParams";
import { SearchSuggestionsOutput } from "types/generated";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import { EXCLUDE_LOGIN_PREDICATE, FILTER_ITEMS_DICTIONARY } from "./constants";
import { SEARCH_POLICIES_SUGGESTIONS } from "./gql";

const EXCLUDED_FILTER_FIELDS = ["name"];
const EXCLUDED_FILTER_VALUES = {
  type: ["LOGIN"],
};

const useSearchPoliciesSuggestions = () => {
  const fieldsRef = useRef<string[] | null>(null);
  const urlParams = useURLParams();
  const searchInput = getSearchQuery(urlParams);

  const predicates = useMemo(() => {
    const predicatesMap = getFiltersPredicationFromURI(urlParams, true);

    return [...(predicatesMap?.values() || []), EXCLUDE_LOGIN_PREDICATE];
  }, [urlParams]);

  const { data, loading, refetch, previousData } = useQuery<{
    searchPoliciesSuggestions: SearchSuggestionsOutput;
  }>(SEARCH_POLICIES_SUGGESTIONS, {
    variables: {
      input: {
        predicates,
        fullTextSearch: searchInput,
        fields: fieldsRef.current,
      },
    },
  });

  const cachedFiltersData = useCachedFilterFields(data?.searchPoliciesSuggestions?.fields);
  const sortOptions = useMemo((): SortOption[] => {
    return cachedFiltersData
      .filter((field) => field.orderable)
      .map((field) => {
        return {
          value: field.name,
          label: field.name,
        };
      });
  }, [cachedFiltersData]);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: FILTER_ITEMS_DICTIONARY,
    excludedFilterFields: EXCLUDED_FILTER_FIELDS,
    excludedFilterValues: EXCLUDED_FILTER_VALUES,
  });

  const handleRefetchActiveSections = (fields: string[]) => {
    fieldsRef.current = fields.length > 0 ? fields : null;

    if (fields.length > 0) {
      refetch({
        input: {
          fullTextSearch: searchInput,
          predicates,
          fields,
        },
      });
    }
  };

  const previousSearchRunsSuggestions = previousData?.searchPoliciesSuggestions;

  return {
    filters,
    filtersMap: filterOptionsMap,
    sortOptions,
    loading,
    refetchActiveSections: handleRefetchActiveSections,
    filteredCount:
      data?.searchPoliciesSuggestions?.filteredCount ||
      previousSearchRunsSuggestions?.filteredCount ||
      0,
  };
};

export default useSearchPoliciesSuggestions;
